import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';

// ============================================================================
// TYPES
// ============================================================================

export interface RolePermissionFormData {
  roleCode: string;
  roleName: string;
  description: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
}

export interface RolePermissionRecord {
  id: string;
  roleCode: string;
  roleName: string;
  description?: string;
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
  isSystemRole: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName?: string;
  updatedAt?: string;
  updatedByName?: string;
  rowVersion: number;
}

// ============================================================================
// CONSTANTS: Available actions per module & navigation sections
// ============================================================================

const MODULE_AVAILABLE_ACTIONS: Record<string, string[]> = {
  'overview': ['view'],
  'gold-rate-master': ['add', 'edit', 'delete', 'activate'],
  'customer-registry': ['add', 'edit', 'delete'],
  'addon-requests': ['add', 'edit', 'delete', 'view', 'submit'],
  'loan-mappings': ['view'],
  'closing-transactions': ['add', 'edit', 'delete', 'view', 'submit'],
  'combined-transactions': ['add', 'edit', 'delete', 'view', 'submit'],
  'regroup-transactions': ['add', 'edit', 'delete', 'view', 'submit'],
  'gl-replacements': ['add', 'edit', 'delete', 'view', 'submit'],
  'approval-dashboard': ['approve', 'return', 'reject'],
  'report-addon-summary': ['view', 'export'],
  'report-loan-mapping': ['view', 'export'],
  'report-closing-settlement': ['view', 'export'],
  'report-combined-txn': ['view', 'export'],
  'report-gl-replacement': ['view', 'export'],
  'report-audit-trail': ['view', 'export'],
  'report-gold-rates': ['view', 'export'],
  'system-config': ['edit'],
  'kfs-calculator': ['view'],
  'entity-master': ['add', 'edit', 'delete'],
  'business-unit-master': ['add', 'edit', 'delete'],
  'user-bu-access': ['add', 'edit', 'revoke'],
  'role-permissions': ['add', 'edit', 'delete', 'view'],
  'user-management': ['reset-password'],
};

const MODULE_LABELS: Record<string, string> = {
  'overview': 'Overview',
  'gold-rate-master': 'Gold Rate Master',
  'customer-registry': 'Customer Registry',
  'addon-requests': 'Add-On Requests',
  'loan-mappings': 'Loan Mappings',
  'closing-transactions': 'Closing Transactions',
  'combined-transactions': 'Combined Transactions',
  'regroup-transactions': 'Regroup Transactions',
  'gl-replacements': 'GL Replacements',
  'approval-dashboard': 'Approval Dashboard',
  'report-addon-summary': 'Add-On Summary',
  'report-loan-mapping': 'Loan Mapping Report',
  'report-closing-settlement': 'Closing Settlement',
  'report-combined-txn': 'Combined Transactions',
  'report-gl-replacement': 'GL Replacements',
  'report-audit-trail': 'Audit Trail',
  'report-gold-rates': 'Gold Rate History',
  'system-config': 'System Configuration',
  'kfs-calculator': 'KFS Calculator',
  'entity-master': 'Entity Master',
  'business-unit-master': 'Branch Master',
  'user-bu-access': 'User Access',
  'role-permissions': 'Role Permissions',
  'user-management': 'User Management',
};

const NAV_SECTIONS = [
  { section: 'Dashboard', modules: ['overview'] },
  { section: 'Masters', modules: ['gold-rate-master', 'customer-registry'] },
  { section: 'Transactions', modules: ['addon-requests', 'loan-mappings', 'closing-transactions', 'combined-transactions', 'regroup-transactions', 'gl-replacements'] },
  { section: 'Approvals', modules: ['approval-dashboard'] },
  { section: 'Reports', modules: ['report-addon-summary', 'report-loan-mapping', 'report-closing-settlement', 'report-combined-txn', 'report-gl-replacement', 'report-audit-trail', 'report-gold-rates'] },
  { section: 'Utilities', modules: ['system-config', 'kfs-calculator'] },
  { section: 'Administration', modules: ['entity-master', 'business-unit-master', 'user-bu-access', 'role-permissions', 'user-management'] },
];

// ============================================================================
// FORM MODAL (Create / Edit)
// ============================================================================

interface RolePermissionFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: RolePermissionFormData;
  isSystemRole?: boolean;
  onFormDataChange: (data: RolePermissionFormData) => void;
  onSave: () => void;
  onClose: () => void;
}

export const RolePermissionFormModal: React.FC<RolePermissionFormModalProps> = ({
  isOpen,
  mode,
  formData,
  isSystemRole = false,
  onFormDataChange,
  onSave,
  onClose,
}) => {
  if (!isOpen) return null;

  const title = mode === 'create' ? 'Create Role Permission' : 'Edit Role Permission';
  const isCodeReadOnly = mode === 'edit' || isSystemRole;
  const isValid = formData.roleCode.trim() !== '' && formData.roleName.trim() !== '';

  const isModuleVisible = (moduleId: string) => formData.visibleModules.includes(moduleId);
  const isActionChecked = (moduleId: string, action: string) =>
    formData.moduleActions[moduleId]?.includes(action) ?? false;

  const toggleModuleVisibility = (moduleId: string) => {
    const newVisibleModules = isModuleVisible(moduleId)
      ? formData.visibleModules.filter((m) => m !== moduleId)
      : [...formData.visibleModules, moduleId];

    const newModuleActions = { ...formData.moduleActions };
    if (!isModuleVisible(moduleId)) {
      // Turning ON — no actions selected yet
    } else {
      // Turning OFF — clear actions
      delete newModuleActions[moduleId];
    }

    onFormDataChange({ ...formData, visibleModules: newVisibleModules, moduleActions: newModuleActions });
  };

  const toggleAction = (moduleId: string, action: string) => {
    const current = formData.moduleActions[moduleId] || [];
    const newActions = current.includes(action)
      ? current.filter((a) => a !== action)
      : [...current, action];

    const newModuleActions = { ...formData.moduleActions };
    if (newActions.length > 0) {
      newModuleActions[moduleId] = newActions;
    } else {
      delete newModuleActions[moduleId];
    }

    onFormDataChange({ ...formData, moduleActions: newModuleActions });
  };

  const toggleAllInSection = (modules: string[], selectAll: boolean) => {
    const newVisibleModules = [...formData.visibleModules];
    const newModuleActions = { ...formData.moduleActions };

    for (const moduleId of modules) {
      if (selectAll) {
        if (!newVisibleModules.includes(moduleId)) newVisibleModules.push(moduleId);
        newModuleActions[moduleId] = [...(MODULE_AVAILABLE_ACTIONS[moduleId] || [])];
      } else {
        const idx = newVisibleModules.indexOf(moduleId);
        if (idx >= 0) newVisibleModules.splice(idx, 1);
        delete newModuleActions[moduleId];
      }
    }

    onFormDataChange({ ...formData, visibleModules: newVisibleModules, moduleActions: newModuleActions });
  };

  const isSectionFullySelected = (modules: string[]) =>
    modules.every((m) => isModuleVisible(m));

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Section 1: Role Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label className="form-label">Role Code *</label>
              <input
                className="form-input"
                value={formData.roleCode}
                onChange={(e) => onFormDataChange({ ...formData, roleCode: e.target.value })}
                placeholder="e.g., lmms_manager"
                disabled={isCodeReadOnly}
                style={isCodeReadOnly ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role Name *</label>
              <input
                className="form-input"
                value={formData.roleName}
                onChange={(e) => onFormDataChange({ ...formData, roleName: e.target.value })}
                placeholder="e.g., Branch Manager"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
              placeholder="Describe the role's purpose..."
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Section 2: Permission Matrix */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Permission Matrix
            </h3>

            {NAV_SECTIONS.map((section) => {
              const allSelected = isSectionFullySelected(section.modules);

              return (
                <div key={section.section} style={{ marginBottom: '1.25rem' }}>
                  {/* Section Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                    padding: '0.4rem 0.6rem',
                    background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                    onClick={() => toggleAllInSection(section.modules, !allSelected)}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => toggleAllInSection(section.modules, !allSelected)}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {section.section}
                    </span>
                  </div>

                  {/* Module Rows */}
                  {section.modules.map((moduleId) => {
                    const visible = isModuleVisible(moduleId);
                    const availableActions = MODULE_AVAILABLE_ACTIONS[moduleId] || [];

                    return (
                      <div
                        key={moduleId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.35rem 0.6rem 0.35rem 1.8rem',
                          gap: '0.75rem',
                          opacity: visible ? 1 : 0.5,
                        }}
                      >
                        {/* Visibility Toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: '180px', cursor: 'pointer', fontSize: '0.82rem' }}>
                          <input
                            type="checkbox"
                            checked={visible}
                            onChange={() => toggleModuleVisibility(moduleId)}
                            style={{ accentColor: 'var(--primary)' }}
                          />
                          {MODULE_LABELS[moduleId] || moduleId}
                        </label>

                        {/* Action Checkboxes */}
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                          {availableActions.map((action) => (
                            <label
                              key={action}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.75rem',
                                color: visible ? 'var(--text-secondary)' : 'var(--text-muted)',
                                cursor: visible ? 'pointer' : 'not-allowed',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isActionChecked(moduleId, action)}
                                onChange={() => toggleAction(moduleId, action)}
                                disabled={!visible}
                                style={{ accentColor: 'var(--primary)' }}
                              />
                              {action}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave} disabled={!isValid}>
            <IconSystem.save size={16} /> {mode === 'create' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// VIEW MODAL (Read-Only)
// ============================================================================

interface RolePermissionViewModalProps {
  isOpen: boolean;
  record: RolePermissionRecord | null;
  onClose: () => void;
}

export const RolePermissionViewModal: React.FC<RolePermissionViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  if (!isOpen || !record) return null;

  const detailStyle: React.CSSProperties = { fontSize: '0.82rem', color: 'var(--text-secondary)' };
  const labelStyle: React.CSSProperties = { fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.15rem' };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">Role Permission Details</h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Role Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div>
              <div style={labelStyle}>Role Code</div>
              <div style={detailStyle}>{record.roleCode}</div>
            </div>
            <div>
              <div style={labelStyle}>Role Name</div>
              <div style={detailStyle}>{record.roleName}</div>
            </div>
            <div>
              <div style={labelStyle}>Type</div>
              <div style={detailStyle}>
                <span style={{
                  padding: '0.15rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  background: record.isSystemRole ? 'rgba(99, 102, 241, 0.15)' : 'rgba(156, 163, 175, 0.15)',
                  color: record.isSystemRole ? '#818cf8' : '#9ca3af',
                }}>
                  {record.isSystemRole ? 'System' : 'Custom'}
                </span>
              </div>
            </div>
            <div>
              <div style={labelStyle}>Status</div>
              <div style={detailStyle}>
                <span style={{
                  padding: '0.15rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  background: record.isActive ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: record.isActive ? '#10b981' : '#ef4444',
                }}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            {record.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={labelStyle}>Description</div>
                <div style={detailStyle}>{record.description}</div>
              </div>
            )}
          </div>

          {/* Permission Matrix (read-only) */}
          <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Permission Matrix
            </h3>

            {NAV_SECTIONS.map((section) => {
              const visibleInSection = section.modules.filter((m) => record.visibleModules.includes(m));
              if (visibleInSection.length === 0) return null;

              return (
                <div key={section.section} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    padding: '0.3rem 0.6rem',
                    background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
                    borderRadius: '6px',
                    marginBottom: '0.4rem',
                  }}>
                    {section.section}
                  </div>

                  {visibleInSection.map((moduleId) => {
                    const actions = record.moduleActions[moduleId] || [];
                    return (
                      <div
                        key={moduleId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.3rem 0.6rem 0.3rem 1.8rem',
                          gap: '0.75rem',
                        }}
                      >
                        <span style={{ fontSize: '0.82rem', minWidth: '180px', color: 'var(--text-secondary)' }}>
                          {MODULE_LABELS[moduleId] || moduleId}
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {actions.map((action) => (
                            <span
                              key={action}
                              style={{
                                padding: '0.1rem 0.4rem',
                                borderRadius: '3px',
                                fontSize: '0.7rem',
                                background: 'rgba(99, 102, 241, 0.12)',
                                color: '#818cf8',
                              }}
                            >
                              {action}
                            </span>
                          ))}
                          {actions.length === 0 && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>visible only</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
